import { useState, useMemo } from "react";
import { useEvents } from "@/hooks/useConflictData";
import { useAuth } from "@/hooks/useAuth";
import { Clock, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const EVENT_ICONS: Record<string, string> = {
  missile: "🚀",
  drone: "🛩️",
  air_raid: "🚨",
  explosion: "💥",
  movement: "🔄",
};

const ConflictTimeline = () => {
  const { isPremium } = useAuth();
  const { data: events = [], isLoading } = useEvents(undefined, isPremium);
  const [expanded, setExpanded] = useState(false);

  const timelineEvents = useMemo(() => {
    const now = Date.now();
    const cutoff = isPremium ? 72 : 24;
    const recent = events.filter((e) => now - new Date(e.occurred_at).getTime() < cutoff * 3600000);
    return (recent.length > 0 ? recent : events)
      .slice()
      .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
      .slice(0, expanded ? 50 : 15);
  }, [events, isPremium, expanded]);

  const getTimeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const getSeverityColor = (type: string) => {
    if (type === "missile") return "border-destructive/40 bg-destructive/5";
    if (type === "explosion") return "border-orange-500/40 bg-orange-500/5";
    if (type === "air_raid") return "border-yellow-500/40 bg-yellow-500/5";
    if (type === "drone") return "border-primary/40 bg-primary/5";
    return "border-border bg-card";
  };

  return (
    <section className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-4 flex items-center gap-3">
          <Clock className="h-5 w-5 text-primary" />
          <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
            Event Timeline
          </h2>
          <Badge variant="outline" className="text-[10px] font-mono border-primary/30 text-primary">
            Last {isPremium ? "72h" : "24h"}
          </Badge>
        </div>

        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

          <div className="space-y-2">
            {timelineEvents.map((event, i) => (
              <div
                key={event.id}
                className={`relative ml-8 rounded-lg border p-3 transition-colors ${getSeverityColor(event.event_type)}`}
              >
                {/* Dot on timeline */}
                <div className="absolute -left-[22px] top-4 h-2.5 w-2.5 rounded-full border-2 border-background bg-primary" />

                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm">{EVENT_ICONS[event.event_type] || "⚠️"}</span>
                      <span className="text-[10px] font-mono uppercase font-bold text-muted-foreground">
                        {event.event_type}
                      </span>
                      {event.country && (
                        <span className="text-[10px] text-muted-foreground">• {event.country}</span>
                      )}
                    </div>
                    <p className="text-sm text-foreground line-clamp-1">{event.title}</p>
                    {event.city && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">{event.city}{event.region ? `, ${event.region}` : ""}</p>
                    )}
                  </div>
                  <span className="shrink-0 text-[10px] font-mono text-muted-foreground whitespace-nowrap">
                    {getTimeAgo(event.occurred_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {timelineEvents.length === 0 && (
            <div className="ml-8 p-8 text-center text-sm text-muted-foreground">
              {isLoading ? "Loading timeline..." : "No events recorded yet. New reports will appear here automatically."}
            </div>
          )}
        </div>

        {timelineEvents.length >= 15 && (
          <div className="mt-4 text-center">
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="gap-1 text-xs">
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {expanded ? "Show less" : "Show more events"}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ConflictTimeline;
